"use client"

import { useEffect, useRef } from "react"
import { Loader2 } from "lucide-react"

import { Button } from "@/components/ui/button"
import { useSharedUiTranslations } from "@/components/i18n/ui-control-labels-context"


/**
 * Bottom only, and only meaningful against the scrolling parent below: `rootMargin`
 * expands the ROOT's rect, so against the document it would be cancelled by the
 * list's own clipping and the next page would never start early.
 */
const PREFETCH_MARGIN = "0px 0px 300px 0px"

export function InfiniteScrollSentinel({
  hasMore,
  isLoading,
  hasFailed = false,
  onLoadMore,
}: {
  hasMore: boolean
  isLoading: boolean
  /** Suspends observing: re-arming over a still-visible sentinel is a retry loop. */
  hasFailed?: boolean
  onLoadMore: () => void
}) {
  const tUi = useSharedUiTranslations("InfiniteScrollSentinel")

  const sentinelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel || !hasMore || hasFailed) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isLoading) onLoadMore()
      },
      { root: scrollParent(sentinel), rootMargin: PREFETCH_MARGIN },
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [hasMore, isLoading, hasFailed, onLoadMore])

  if (!hasMore) return null

  return (
    <div ref={sentinelRef} role="status" className="flex justify-center py-3">
      {hasFailed ? (
        <Button variant="outline" size="sm" onClick={onLoadMore}>
          <span key={tUi("loadMore")}>{tUi("loadMore")}</span></Button>
      ) : (
        isLoading && (
          <>
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            {/* The button this replaced said so; a bare icon says nothing. */}
            <span className="sr-only"><span key={tUi("loadingMoreConversations")}>{tUi("loadingMoreConversations")}</span></span>
          </>
        )
      )}
    </div>
  )
}

function scrollParent(node: HTMLElement): HTMLElement | null {
  for (let element = node.parentElement; element; element = element.parentElement) {
    const { overflowY } = getComputedStyle(element)
    if (overflowY === "auto" || overflowY === "scroll") return element
  }
  return null
}
